import { FC, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { ConfirmationModal } from '@components';
import {
  komplyanceAiApiService,
  useConversationsHistory,
  useDeleteConversation,
} from '@features/komplyance-ai';
import { useBackendErrorToast, useInjection } from '@hooks';
import { Trans, useTrans } from '@libs/i18n';
import { KomplyanceAiGetConversationsListItem } from '@libs/types';
import { TrashIcon } from '@phosphor-icons/react';
import { ConversationsHistoryStore, StreamingStore } from '@stores';

import { DI_TOKENS } from '../../../../constants';

import { ChatHistoryCard } from './ChatHistoryCard';

type Section = 'today' | 'lastWeek' | 'lastMonth' | 'older';

const SECTION_ORDER: Section[] = ['today', 'lastWeek', 'lastMonth', 'older'];

const SECTION_LABELS: Record<Section, string> = {
  today: 'Today',
  lastWeek: 'Last 7 days',
  lastMonth: 'Last 30 days',
  older: 'Older',
};

const getSection = (timestamp: number): Section => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const date = timestamp * 1000;

  if (date >= startOfToday) return 'today';
  if (date >= startOfToday - 6 * 86400_000) return 'lastWeek';
  if (date >= startOfToday - 29 * 86400_000) return 'lastMonth';
  return 'older';
};

export const ChatHistoryPanel: FC = observer(() => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  const { data: conversations, isLoading, error } = useConversationsHistory();
  const conversationsHistoryStore = useInjection<ConversationsHistoryStore>(
    DI_TOKENS.ConversationsHistoryStore,
  );
  const streamingStore = useInjection<StreamingStore>(DI_TOKENS.StreamingStore);

  const [pendingDelete, setPendingDelete] = useState<KomplyanceAiGetConversationsListItem | null>(
    null,
  );
  const showDeleteError = useBackendErrorToast('chatHistory.toast.deleteFailed');
  const deleteConversationMutation = useDeleteConversation();

  const currentConversation = conversationsHistoryStore.currentConversation;
  const isEmpty = !conversations || conversations.data?.length === 0;

  const grouped = useMemo(() => {
    const sorted =
      conversations?.data
        ?.slice()
        .sort((a, b) => b.updated_at - a.updated_at)
        .filter((c) =>
          c.name.toLowerCase().includes(conversationsHistoryStore.searchTerm.toLowerCase()),
        ) ?? [];

    const groups: Record<Section, KomplyanceAiGetConversationsListItem[]> = {
      today: [],
      lastWeek: [],
      lastMonth: [],
      older: [],
    };
    for (const c of sorted) {
      groups[getSection(c.updated_at)].push(c);
    }
    return groups;
  }, [conversations, conversationsHistoryStore.searchTerm]);

  const onCardClick = (conversation: KomplyanceAiGetConversationsListItem) => {
    conversationsHistoryStore.selectConversation(conversation);
    navigate(`/chat/${conversation.id}`);
  };

  const onConfirmDelete = async () => {
    if (!pendingDelete) return;
    const { id } = pendingDelete;

    // Stop the generation first: a delete mid-stream leaves the AI service
    // writing messages to a conversation that no longer exists.
    if (streamingStore.isStreamActive(id)) {
      try {
        await komplyanceAiApiService.abortStream(id, streamingStore.getStream(id)?.taskId);
      } catch (abortError) {
        // The delete is what matters; a failed abort must not block it.
        console.error(`Error aborting stream for ${id}`, abortError);
      }
    }

    try {
      await deleteConversationMutation.mutateAsync(id);

      // Only once the server has confirmed the delete. Dropping the local
      // stream state before that would leave a failed delete with a card back
      // in the list but its partial answer and un-persisted user message gone.
      streamingStore.clearPendingUserMessage(id);
      streamingStore.cleanupStream(id);

      enqueueSnackbar(t('chatHistory.toast.deleted'), { variant: 'success' });

      // Staying on /chat/<deleted-id> renders a blank, title-less conversation:
      // the lookup answers 200 with empty data, which is not an error state.
      if (pathname === `/chat/${id}`) {
        conversationsHistoryStore.deselectConversation();
        navigate('/chat');
      }
    } catch (deleteError) {
      showDeleteError(deleteError);
    } finally {
      setPendingDelete(null);
    }
  };

  // Rendered from every branch below, not just the populated list: deleting the
  // last conversation empties the optimistically patched cache, and a modal
  // living only in the populated branch would unmount itself mid-request.
  const deleteConfirmation = pendingDelete ? (
    <ConfirmationModal
      open
      onClose={() => setPendingDelete(null)}
      title={t('chatHistory.delete.title')}
      icon={<TrashIcon size={20} />}
      iconColor="error"
      isLoading={deleteConversationMutation.isPending}
      content={
        <Typography variant="body2" color="text.secondary">
          {t('chatHistory.delete.body', { name: pendingDelete.name })}
        </Typography>
      }
      buttons={[
        {
          label: t('app.common.cancel'),
          onClick: () => setPendingDelete(null),
          variant: 'outlined',
          color: 'secondary',
        },
        {
          label: t('chatHistory.delete.confirm'),
          onClick: onConfirmDelete,
          variant: 'contained',
          color: 'error',
          isLoading: deleteConversationMutation.isPending,
        },
      ]}
    />
  ) : null;

  if (isLoading && isEmpty) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          <Trans i18nKey="chatHistory.loadingConversations" />
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: '600', fontSize: '1rem' }}>
          <Trans i18nKey="chatHistory.sidePanel.title" />
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message || <Trans i18nKey="chatHistory.errors.failedToLoad" />}
        </Alert>
      </Box>
    );
  }

  if (isEmpty) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
            p: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: '600', fontSize: '1rem' }}>
            <Trans i18nKey="chatHistory.sidePanel.title" />
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            <Trans i18nKey="chatHistory.emptyState.noConversations" />
          </Typography>
        </Box>
        {deleteConfirmation}
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography
        variant="h6"
        sx={{
          mb: 1.5,
          fontWeight: '600',
          fontSize: '1rem',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#fff',
        }}
      >
        <Trans i18nKey="chatHistory.sidePanel.title" />
      </Typography>
      <Box
        sx={{
          flex: 1,
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {SECTION_ORDER.every((s) => grouped[s].length === 0) && (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4, px: 2 }}>
            <Trans i18nKey="chatHistory.emptyState.noSearchResults" />
          </Typography>
        )}
        {SECTION_ORDER.filter((s) => grouped[s].length > 0).map((section) => (
          <Box key={section} sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#ADADAD',
                fontWeight: 600,
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                px: 1.25,
                mb: 0.5,
              }}
            >
              {SECTION_LABELS[section]}
            </Typography>
            {grouped[section].map((conversation) => (
              <ChatHistoryCard
                key={conversation.id}
                title={conversation.name}
                isActive={currentConversation?.id === conversation.id}
                isStreaming={streamingStore.isStreamActive(conversation.id)}
                onClick={() => onCardClick(conversation)}
                onDelete={() => setPendingDelete(conversation)}
              />
            ))}
          </Box>
        ))}
      </Box>

      {deleteConfirmation}
    </Box>
  );
});
